import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Heart, MapPin, Truck, Trophy, ShoppingCart, ChevronLeft } from "lucide-react";
import { api, errMsg, fmt, fmtDate } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { DemoBadge, Spinner, Stars, btnPrimary, inputCls } from "../components/ui";

function OfferRow({ o, best, product }) {
  const { add } = useCart();
  const total = o.price + (o.store.delivery_fee || 0);
  return (
    <tr className={`border-b border-gray-100 ${best ? "bg-bo-green/5" : ""}`} data-testid={`offer-row-${o.id}`}>
      <td className="py-3 pl-4 pr-2">
        <p className="flex flex-wrap items-center gap-1.5 font-semibold">
          {o.store.name}
          {o.store.is_own ? <span className="rounded-full bg-bo-green px-2 py-0.5 text-[10px] font-bold text-white">Venda própria</span>
            : <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-bold text-gray-600">Parceiro</span>}
          {best && <span className="inline-flex items-center gap-1 rounded-full bg-bo-yellow px-2 py-0.5 text-[10px] font-bold"><Trophy className="h-3 w-3" />Melhor total</span>}
        </p>
        <p className="mt-0.5 flex items-center gap-1 text-xs text-gray-500"><MapPin className="h-3 w-3" />{o.store.location}</p>
        <Stars value={o.store.rating} />
      </td>
      <td className="px-2 text-right">
        <p className="font-bold text-bo-green" data-testid="offer-price">{fmt(o.price)}</p>
        {o.old_price > o.price && <p className="text-xs text-gray-400 line-through">{fmt(o.old_price)}</p>}
      </td>
      <td className="hidden px-2 text-right text-sm text-gray-600 sm:table-cell">
        <p className="flex items-center justify-end gap-1"><Truck className="h-3.5 w-3.5" />{fmt(o.store.delivery_fee)}</p>
        <p className="text-xs text-gray-400">{o.store.delivery_days <= 1 ? "24 horas" : `${o.store.delivery_days} dias`}</p>
      </td>
      <td className="hidden px-2 text-center text-sm md:table-cell" data-testid="offer-stock">
        {o.stock > 0 ? <span className={o.stock <= 5 ? "font-semibold text-amber-600" : "text-gray-600"}>{o.stock} un.</span> : <span className="font-semibold text-red-600">Esgotado</span>}
      </td>
      <td className="px-2 text-right font-bold" data-testid="offer-total">{fmt(total)}</td>
      <td className="py-3 pl-2 pr-4 text-right">
        <button disabled={o.stock <= 0} onClick={() => add(product, o)} className={`${btnPrimary} px-3 py-2`} data-testid={`offer-add-${o.id}`}>
          <ShoppingCart className="h-4 w-4" /><span className="hidden lg:inline">Adicionar</span>
        </button>
      </td>
    </tr>
  );
}

function Reviews({ product, onAdded }) {
  const { user } = useAuth();
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/products/${product.id}/reviews`, { rating, comment });
      toast.success("Avaliação publicada");
      setComment("");
      onAdded();
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <section className="bo-card p-5" data-testid="reviews-section">
      <h2 className="mb-3 text-lg font-bold">Avaliações ({product.reviews.length})</h2>
      {user && (
        <form onSubmit={submit} className="mb-5 flex flex-col gap-2 rounded-xl bg-bo-bg p-3" data-testid="review-form">
          <p className="text-xs text-gray-500">Só pode avaliar produtos de encomendas já entregues.</p>
          <div className="flex items-center gap-2">
            <select value={rating} onChange={(e) => setRating(Number(e.target.value))} className={`${inputCls} w-28`} data-testid="review-rating-select">
              {[5, 4, 3, 2, 1].map((r) => <option key={r} value={r}>{r} estrelas</option>)}
            </select>
            <input value={comment} onChange={(e) => setComment(e.target.value)} maxLength={600} placeholder="O seu comentário (opcional)" className={inputCls} data-testid="review-comment-input" />
            <button className={btnPrimary} data-testid="review-submit-btn">Publicar</button>
          </div>
        </form>
      )}
      {!product.reviews.length && <p className="text-sm text-gray-500">Ainda não há avaliações verificadas.</p>}
      <div className="flex flex-col gap-3">
        {product.reviews.map((r) => (
          <div key={r.id} className="border-b border-gray-100 pb-3" data-testid={`review-${r.id}`}>
            <div className="flex items-center gap-2"><span className="font-semibold">{r.user_name}</span><Stars value={r.rating} /><span className="text-xs text-gray-400">{fmtDate(r.created_at)}</span></div>
            {r.comment && <p className="mt-1 text-sm text-gray-700">{r.comment}</p>}
          </div>
        ))}
      </div>
    </section>
  );
}

export default function ProductPage() {
  const { id } = useParams();
  const { user, setUser } = useAuth();
  const [p, setP] = useState(null);
  const load = () => api.get(`/products/${id}`).then(({ data }) => setP(data)).catch(() => setP(false));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (p === null) return <Spinner />;
  if (!p) return <p className="bo-card p-8 text-center" data-testid="product-not-found">Produto não encontrado.</p>;
  const fav = user?.favorites?.includes(p.id);
  const toggleFav = async () => {
    if (!user) return toast.info("Entre na sua conta para guardar favoritos");
    const { data } = await api.post(`/favorites/${p.id}`);
    setUser({ ...user, favorites: data.favorite ? [...(user.favorites || []), p.id] : user.favorites.filter((f) => f !== p.id) });
  };
  return (
    <div className="flex flex-col gap-5" data-testid="product-page">
      <Link to="/catalogo" className="flex w-fit items-center gap-1 text-sm text-gray-500 hover:text-bo-green" data-testid="back-to-catalog"><ChevronLeft className="h-4 w-4" />Voltar ao catálogo</Link>
      <div className="bo-card grid gap-6 p-5 md:grid-cols-[minmax(0,380px)_1fr]">
        <div className="relative aspect-square overflow-hidden rounded-2xl bg-gray-50">
          <img src={p.image} alt={p.name} className="h-full w-full object-cover" />
          {p.is_demo && <DemoBadge className="absolute right-3 top-3" />}
        </div>
        <div className="flex flex-col gap-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-bo-green">{p.category_name} · {p.brand}</p>
          <h1 className="text-2xl font-extrabold leading-tight sm:text-3xl" data-testid="product-name">{p.name}</h1>
          <Stars value={p.rating} count={p.reviews_count} />
          <p className="text-sm text-gray-600">{p.description}</p>
          <div className="flex flex-wrap items-end gap-4 rounded-2xl bg-bo-bg p-4">
            <div>
              <p className="text-xs text-gray-500">A partir de</p>
              <p className="text-3xl font-extrabold text-bo-green" data-testid="product-min-price">{fmt(p.min_price)}</p>
            </div>
            <p className="text-sm text-gray-600">em <strong>{p.offers_count}</strong> {p.offers_count === 1 ? "loja" : "lojas"} · {p.unit}</p>
          </div>
          <button onClick={toggleFav} className={`flex w-fit items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium ${fav ? "border-red-200 bg-red-50 text-red-600" : "border-gray-200 hover:border-bo-green"}`} data-testid="favorite-toggle-btn">
            <Heart className={`h-4 w-4 ${fav ? "fill-red-500" : ""}`} />{fav ? "Nos favoritos" : "Guardar nos favoritos"}
          </button>
        </div>
      </div>
      <section className="bo-card overflow-hidden" data-testid="offers-comparison">
        <div className="border-b border-gray-100 px-4 py-3">
          <h2 className="text-lg font-bold">Comparar lojas</h2>
          <p className="text-xs text-gray-500">Total = preço do produto + custo de entrega da loja.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-500">
              <tr><th className="py-2 pl-4 text-left">Loja</th><th className="px-2 text-right">Preço</th><th className="hidden px-2 text-right sm:table-cell">Entrega</th><th className="hidden px-2 md:table-cell">Stock</th><th className="px-2 text-right">Total</th><th /></tr>
            </thead>
            <tbody>{p.offers.map((o, i) => <OfferRow key={o.id} o={o} best={i === 0 && o.stock > 0} product={p} />)}</tbody>
          </table>
        </div>
      </section>
      <Reviews product={p} onAdded={load} />
    </div>
  );
}
